import { AudioContextManager } from './audio-context-manager';

// Safe Web Audio API initialization utility using AudioContextManager singleton
export function createAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;

  try {
    const manager = AudioContextManager.getInstance();
    let ctx = (manager as any).ctx as AudioContext | null;
    if (ctx && ctx.state !== 'closed') {
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      return ctx;
    }

    // Synchronously instantiate AudioContext on direct user gesture
    const AudioCtxClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

    if (AudioCtxClass) {
      try {
        ctx = new AudioCtxClass({ latencyHint: 'interactive' });
      } catch {
        ctx = new AudioCtxClass();
      }
      (manager as any).ctx = ctx;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      return ctx;
    }
  } catch (e) {
    console.warn("AudioContext creation safely intercepted:", e);
  }
  return null;
}

/**
  * Check if AudioWorkletNode is supported and allowed in current browser context
  */
export function hasAudioWorkletSupport(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    return Boolean(
      typeof window.AudioWorkletNode !== 'undefined' &&
      AudioCtx &&
      AudioCtx.prototype &&
      'audioWorklet' in AudioCtx.prototype
    );
  } catch {
    return false;
  }
}

/**
  * Check if WebAssembly memory allocation and execution is supported and allowed
  */
export function hasWasmSupport(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function') {
      const module = new WebAssembly.Module(
        Uint8Array.of(0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00)
      );
      return module instanceof WebAssembly.Module;
    }
  } catch {
    console.warn('WebAssembly memory allocation restricted in current browser environment.');
  }
  return false;
}

export interface PitchDetectionResult {
  pitch: number;
  clarity: number;
  accent?: string;
}

/**
  * Fallback YIN Pitch Detector using ScriptProcessorNode when AudioWorklet is restricted
  */
export function createScriptProcessorPitchDetector(
  ctx: AudioContext,
  sourceNode: AudioNode,
  onPitch: (data: PitchDetectionResult) => void,
  bufferSize: number = 2048
): { node: ScriptProcessorNode; stop: () => void } {
  // Use createScriptProcessor fallback for restricted environments
  const scriptNode = ctx.createScriptProcessor(bufferSize, 1, 1);
  const buffer = new Float32Array(bufferSize);
  let bufferIndex = 0;

  scriptNode.onaudioprocess = (event: AudioProcessingEvent) => {
    const input = event.inputBuffer.getChannelData(0);
    for (let i = 0; i < input.length; i++) {
      buffer[bufferIndex++] = input[i];
      if (bufferIndex >= bufferSize) {
        const result = detectYinPitch(buffer, ctx.sampleRate);
        onPitch(result);
        bufferIndex = 0;
      }
    }
  };

  sourceNode.connect(scriptNode);
  scriptNode.connect(ctx.destination);

  return {
    node: scriptNode,
    stop: () => {
      try {
        scriptNode.disconnect();
        sourceNode.disconnect(scriptNode);
      } catch {}
    },
  };
}

/**
  * Pure JS YIN pitch estimation for fallback execution
  */
function detectYinPitch(buffer: Float32Array, sampleRate: number): PitchDetectionResult {
  let rms = 0;
  for (let i = 0; i < buffer.length; i++) {
    rms += buffer[i] * buffer[i];
  }
  rms = Math.sqrt(rms / buffer.length);

  if (rms < 0.01) {
    return { pitch: 0, clarity: 0, accent: 'SAMANYA' };
  }

  const W = Math.floor(buffer.length / 2);
  const d = new Float32Array(W);

  for (let tau = 0; tau < W; tau++) {
    let sum = 0;
    for (let j = 0; j < W; j++) {
      const delta = buffer[j] - buffer[j + tau];
      sum += delta * delta;
    }
    d[tau] = sum;
  }

  const dPrime = new Float32Array(W);
  dPrime[0] = 1;
  let runningSum = 0;

  for (let tau = 1; tau < W; tau++) {
    runningSum += d[tau];
    dPrime[tau] = runningSum === 0 ? 0 : (d[tau] * tau) / runningSum;
  }

  const threshold = 0.15;
  let tauEstimate = -1;
  const minLag = Math.floor(sampleRate / 1000);
  const maxLag = Math.floor(sampleRate / 50);

  for (let tau = minLag; tau < Math.min(W - 1, maxLag); tau++) {
    if (dPrime[tau] < threshold) {
      while (tau + 1 < W && dPrime[tau + 1] < dPrime[tau]) {
        tau++;
      }
      tauEstimate = tau;
      break;
    }
  }

  if (tauEstimate === -1) {
    let minVal = Infinity;
    for (let tau = minLag; tau < Math.min(W - 1, maxLag); tau++) {
      if (dPrime[tau] < minVal) {
        minVal = dPrime[tau];
        tauEstimate = tau;
      }
    }
    if (minVal > 0.4) {
      return { pitch: 0, clarity: 0, accent: 'SAMANYA' };
    }
  }

  let betterTau = tauEstimate;
  if (tauEstimate > 0 && tauEstimate < W - 1) {
    const s0 = dPrime[tauEstimate - 1];
    const s1 = dPrime[tauEstimate];
    const s2 = dPrime[tauEstimate + 1];
    const denom = 2 * (2 * s1 - s0 - s2);
    if (denom !== 0) {
      betterTau = tauEstimate + (s2 - s0) / denom;
    }
  }

  const pitchHz = Math.round((sampleRate / betterTau) * 10) / 10;
  const clarity = Math.min(1.0, Math.max(0, 1.0 - dPrime[tauEstimate]));

  let accent = 'SAMANYA';
  if (pitchHz > 260) {
    accent = 'UDATTA';
  } else if (pitchHz > 0 && pitchHz < 190) {
    accent = 'ANUDATTA';
  } else if (pitchHz >= 190 && pitchHz <= 260) {
    accent = 'SVARITA';
  }

  return {
    pitch: pitchHz,
    clarity: Math.round(clarity * 100) / 100,
    accent,
  };
}
